import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { runAllMigrations } from "@/api/run-migrations";
import DashboardLayout from "../dashboard/layout/DashboardLayout";

export default function RunMigrationsPage() {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleRunMigrations = async () => {
    setIsRunning(true);
    setResult(null);

    try {
      const response = await runAllMigrations();

      if (response.success) {
        setResult({
          success: true,
          message: "Migrations completed successfully!",
        });
      } else {
        setResult({
          success: false,
          message:
            response.error ||
            "Failed to run migrations. Please check the console for details.",
        });
      }
    } catch (error) {
      console.error("Error running migrations:", error);
      setResult({
        success: false,
        message:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <DashboardLayout activeItem="Settings">
      <div className="container mx-auto py-8">
        <Card className="w-full max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Database Migrations</CardTitle>
            <CardDescription>
              Run all pending database migrations to update your schema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-muted-foreground">
              This will apply all pending migrations to your database. Make sure
              you have a backup before proceeding as this action cannot be
              undone.
            </p>

            {result && (
              <Alert
                className={`mt-4 ${result.success ? "bg-good-bg border-good-line" : "bg-danger-bg border-danger-line"}`}
              >
                {result.success ? (
                  <CheckCircle className="h-4 w-4 text-good" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-danger" />
                )}
                <AlertTitle>{result.success ? "Success" : "Error"}</AlertTitle>
                <AlertDescription>{result.message}</AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter>
            <Button
              onClick={handleRunMigrations}
              disabled={isRunning}
              className="w-full"
            >
              {isRunning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running Migrations...
                </>
              ) : (
                "Run Migrations"
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </DashboardLayout>
  );
}
